"use client";
import * as React from "react";
import {
  Stack, Typography, Chip, Button, CircularProgress, Snackbar, Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  cancelSchedule, pauseSchedule, resumeSchedule, type ScheduleSummary,
} from "../../api/schedules";
import { WorkspaceCard } from "../Common/WorkspaceCard";
import { ConfirmDialog } from "../Common/ConfirmDialog";
import { usePortalScope } from "@/analytics/PortalScope";
import { ScheduleHistoryDialog } from "./ScheduleHistoryDialog";
import { humanizeCron } from "./cron";

interface Props {
  schedule: ScheduleSummary;
  onChanged: () => void;
}

/** Human-readable label for the schedule's kind chip. */
function kindLabel(s: ScheduleSummary): string {
  switch (s.kind) {
    case "cron": return "Recurring";
    case "one_shot": return "One-time";
    case "event": return "On activity";
    default: return s.kind;
  }
}

/** The descriptive timing line under the name. */
function summaryLine(s: ScheduleSummary): string {
  if (s.kind === "cron" && s.cron_expr) return humanizeCron(s.cron_expr);
  if (s.kind === "one_shot" && s.next_run_at) {
    return `Runs ${new Date(s.next_run_at).toLocaleString()}`;
  }
  if (s.kind === "event") return "Runs on conversation activity";
  return "One-time";
}

export function ScheduleRow({ schedule, onChanged }: Props) {
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const cancelDialogScope = usePortalScope("schedule-delete_dialog")["data-ph-scope"];
  const [pausing, setPausing] = React.useState(false);
  // Inline error toast (replaces window.alert) for the pause/resume action.
  const [toast, setToast] = React.useState<string | null>(null);
  const isPaused = !!schedule.paused_at;

  // Runs inside ConfirmDialog: it shows the spinner + surfaces any throw
  // inline, so we just do the work and close on success.
  async function confirmCancel() {
    await cancelSchedule(schedule.id);
    setConfirmOpen(false);
    onChanged();
  }

  async function handleTogglePause() {
    setPausing(true);
    try {
      if (isPaused) await resumeSchedule(schedule.id);
      else await pauseSchedule(schedule.id);
      onChanged();
    } catch (e) {
      setToast(e instanceof Error ? e.message : String(e));
    } finally {
      setPausing(false);
    }
  }

  return (
    <>
      <WorkspaceCard
        data-ph-scope="schedule"
        sx={pausing ? { opacity: 0.7 } : undefined}
      >
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <Stack spacing={0.5}>
            <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {schedule.name}
              </Typography>
              <Chip
                label={kindLabel(schedule)}
                size="small"
                // Recurring → teal primary; one-time / event → terracotta
                // tertiary, so both brand hues appear in the list.
                sx={(t) => {
                  const c =
                    schedule.kind === "cron"
                      ? t.palette.primary
                      : t.palette.tertiary;
                  return {
                    bgcolor: alpha(c.main, 0.16),
                    color: c.main,
                    border: `1px solid ${alpha(c.main, 0.32)}`,
                    fontWeight: 600,
                  };
                }}
              />
              {isPaused && (
                <Chip
                  label="Paused"
                  size="small"
                  variant="outlined"
                  sx={(t) => ({
                    color: t.palette.tertiary.main,
                    borderColor: alpha(t.palette.tertiary.main, 0.5),
                  })}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {summaryLine(schedule)}
            </Typography>
            {schedule.last_run_at && (
              <Typography variant="caption" color="text.secondary">
                Last run: {new Date(schedule.last_run_at).toLocaleString()}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
            <Button size="small" variant="outlined" data-ph="open_history" onClick={() => setHistoryOpen(true)}>
              History
            </Button>
            {/* Event schedules have no time window to pause meaningfully today,
                but pausing still stops them firing — offered for all kinds. */}
            <Button
              size="small"
              variant="outlined"
              disabled={pausing}
              // Named by what the click will do; pause/resume are not shared verbs.
              data-ph={isPaused ? "toggle_resume" : "toggle_pause"}
              onClick={handleTogglePause}
              startIcon={pausing ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {isPaused ? "Resume" : "Pause"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              // Cancelling a schedule removes it for good (rule 5 keeps `cancel`
              // for closing a form or stopping a run), so it is a delete.
              data-ph="delete"
              onClick={() => setConfirmOpen(true)}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </WorkspaceCard>
      <ScheduleHistoryDialog
        scheduleId={schedule.id}
        scheduleName={schedule.name}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Cancel this scheduled task?"
        body={
          <>
            <strong>{schedule.name}</strong> will be cancelled and won’t run again.
            This can’t be undone.
          </>
        }
        confirmLabel="Cancel task"
        cancelLabel="Keep it"
        destructive
        onConfirm={confirmCancel}
        onClose={() => setConfirmOpen(false)}
        scope={cancelDialogScope}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setToast(null)} variant="filled">
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}
